import { Link } from "@tanstack/react-router";
import { FileCheck2 } from "lucide-react";
import { useMe } from "@/features/auth/useMe";

/**
 * Shell-mounted prompt for the re-consent loop: when a signed-in family
 * member has a team whose consent is stale (DOC_VERSION bumped), missing
 * (membership predates capture), or incomplete (a child was linked
 * after they consented), a slim banner points them at
 * /me?tab=consent, where the affirmation actually happens.
 *
 * Reads `/api/me` — it does NOT issue a query of its own. This
 * component renders on every page for every signed-in user, and
 * `queryClient`'s global handler signs the user out on ANY 401, so a
 * dedicated authenticated request here turned a single transient 401
 * on a non-critical prompt into an app-wide sign-out (it also broke
 * every e2e spec that stubs `/api/me` without stubbing the new route).
 * The prompt data now rides on the auth payload that is already the
 * source of truth for "am I signed in".
 */
export function ConsentReaffirmBanner() {
  const me = useMe();
  const pending = me.data?.consent_needs_affirmation ?? [];
  if (!me.data?.user || pending.length === 0) return null;

  const teamNames = pending.map((g) => g.team_name).filter(Boolean).join(", ");
  const headline = pending.some((g) => g.status === "stale")
    ? "The beta documents were updated — please re-confirm consent"
    : pending.some((g) => g.status === "incomplete")
      ? "A player was added to your account — consent is needed"
      : "Consent is needed";

  return (
    <div
      data-testid="consent-reaffirm-banner"
      role="status"
      className="flex flex-wrap items-center justify-center gap-2 border-b border-accent/40 bg-accent/10 px-4 py-2 text-sm text-ink"
    >
      <FileCheck2 size={15} strokeWidth={2.2} aria-hidden="true" />
      <span>
        {headline}
        {teamNames ? ` for ${teamNames}` : ""}.
      </span>
      <Link
        to="/me"
        search={{ tab: "consent" }}
        className="font-medium text-accent hover:underline"
      >
        Review and confirm
      </Link>
    </div>
  );
}
